import { Organizations } from 'libs/end-to-end/src/types/organizations/organizations';
import React, { useCallback, useMemo, useState } from 'react';
import { AdminLayout } from 'apps/sigma2-ui/src/components/AdminLayout';
import { AdminTable } from 'apps/sigma2-ui/src/components/AdminLayout';
import { AdminTableRow } from 'apps/sigma2-ui/src/components/AdminLayout';
import { LegacyButtonVariant } from 'apps/sigma2-ui/src/components/ui/Buttons';
import { LinkButton } from 'apps/sigma2-ui/src/components/ui/LinkButton';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { Dialog } from '@headlessui/react';
import { Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { LegacyButton } from 'apps/sigma2-ui/src/components/ui/LegacyButton';
import { EmptyObject } from 'libs/end-to-end/src/generic';
import { TextInput } from 'apps/sigma2-ui/src/components/ui/TextInput';
import { getOrganizationLogoUrl } from 'apps/sigma2-ui/src/organizations/utils/get-organization-logo-url';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { Spinner } from 'apps/sigma2-ui/src/components/ui/Spinner';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { useAPIMetaInformation } from 'apps/sigma2-ui/src/state/meta-information';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { AdminBreadcrumbs } from 'apps/sigma2-ui/src/components/ui/AdminBreadcrumbs';

/**
 * OrganizationsPage component
 *
 * This component renders the Organizations management page, allowing users to view, create, edit, and delete organizations.
 * It includes a table of organizations and a modal dialog for confirming organization deletion.
 */
export const OrganizationsPage = () => {
  const language = useLanguage();
  const { userAuthenticatedApiCall } = useUser();
  const { metaInformation } = useAPIMetaInformation();

  // State for managing the delete organization prompt
  const [deleteOrgPrompt, setDeleteOrgPrompt] = useState<string | null>(null);
  const [isDeletingOrg, setIsDeletingOrg] = useState(false);

  // Add new state for error modal
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  /**
   * Callback to get the URL for fetching organizations
   */
  const getUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations`;
  }, []);

  /**
   * Hook for fetching and managing organization data
   */
  const readPatternHook = useSimpleReadPattern({
    getUrlFn,
    canRead: true,
    responseSchema: Organizations,
  });

  /**
   * Function to refresh the organizations data
   */
  const refreshOrganizations = useCallback(() => {
    if (
      readPatternHook.status === 'done' ||
      readPatternHook.status === 'error'
    ) {
      readPatternHook.reloadEntity();
    }
  }, [readPatternHook]);

  /**
   * Async callback for deleting an organization
   */
  const deleteOrganization = useAsyncCallback(
    async (isAlive) => {
      if (!isAlive() || !deleteOrgPrompt) {
        return;
      }

      try {
        setIsDeletingOrg(true);

        const deleteOrgResult = await userAuthenticatedApiCall({
          url: `${getApiBaseUrl()}/organizations/${deleteOrgPrompt}`,
          requestOptions: {
            method: 'DELETE',
          },
          responseSchema: EmptyObject,
        });

        if (!isAlive() || !deleteOrgResult) {
          return;
        }

        if (
          deleteOrgResult.resultType === 'err' &&
          deleteOrgResult.error.errorType === 'ApiError'
        ) {
          const errorReason =
            deleteOrgResult.error.errorReason &&
            typeof deleteOrgResult.error.errorReason === 'string'
              ? deleteOrgResult.error.errorReason
              : 'An error occurred';

          setErrorMessage(errorReason);
          setDeleteConfirmText('');
          return;
        }

        refreshOrganizations();
        // Clear the deleteConfirmText after successful deletion
        setDeleteConfirmText('');
      } finally {
        setIsDeletingOrg(false);
        setDeleteOrgPrompt(null);
      }
    },
    [deleteOrgPrompt, userAuthenticatedApiCall, refreshOrganizations],
  );

  /**
   * Memoized table rows for the organizations
   */
  const rows = useMemo(() => {
    if (readPatternHook.status !== 'done' || metaInformation === null) {
      return null;
    }

    const actualRows: AdminTableRow[] = [];

    for (const organization of readPatternHook.entity.organizations) {
      const orgLogoUrl = getOrganizationLogoUrl({
        organization,
        publicDynamicAssetsDomainName:
          metaInformation.publicDynamicAssetsDomainName,
      });

      actualRows.push({
        key: organization.id,
        columns: [
          {
            columnType: 'custom',
            key: 'logo',
            ui: orgLogoUrl ? (
              <div className="flex items-center justify-center">
                <img
                  src={orgLogoUrl}
                  alt={`${organization.name} logo`}
                  className="h-8 w-8 object-contain"
                />
              </div>
            ) : (
              <div className="flex items-center justify-center">
                <span className="text-gray-400">No logo</span>
              </div>
            ),
          },
          {
            columnType: 'text',
            key: 'name',
            text: organization.name,
          },
          {
            columnType: 'custom',
            key: 'edit',
            ui: (
              <Button
                behavior={{
                  type: 'href',
                  href: `/organizations/edit/${organization.id}`,
                }}
                text={'Edit'}
              />
            ),
          },
          {
            columnType: 'button',
            key: 'delete',
            buttonText: 'Delete',
            onClick: () => setDeleteOrgPrompt(organization.id),
            ariaLabel: `Delete ${organization.name}`,
          },
        ],
      });
    }
    return actualRows;
  }, [metaInformation, readPatternHook]);

  return (
    <AdminLayout currentPage={'Organizations'}>
      <AdminBreadcrumbs
        items={[
          { label: 'Home', to: '/admin' },
          { label: 'Organizations', to: '/organizations' },
        ]}
      />
      <p>You can manage organizations from this screen.</p>

      {isDeletingOrg ? (
        <div className="mt-4 text-gray-600">Deleting organization...</div>
      ) : rows !== null ? (
        <>
          <S2Typography
            variant="headline-4"
            translatedContent={language.organizationsPageTableTitle()}
          />
          <div className="flex flex-col items-start gap-2">
            <LinkButton
              variant={LegacyButtonVariant.PRIMARY_LIGHT}
              href="/organizations/new"
              data-qa="newUserBtn"
              className="mt-4"
            >
              New Organization
            </LinkButton>
            <LinkButton
              variant={LegacyButtonVariant.PRIMARY_LIGHT}
              href="/organization-private-networking-entitlements"
              data-qa="privateNetworkingEntitlementsBtn"
            >
              Private Networking Entitlements
            </LinkButton>
          </div>
          <AdminTable
            headings={['Logo', 'Name', 'Edit', 'Delete']}
            rows={rows}
          />
        </>
      ) : (
        <Spacing direction="horizontal" size="sm">
          <Spinner size={24} />
        </Spacing>
      )}

      {/* Delete organization confirmation dialog */}
      <Transition appear show={!!deleteOrgPrompt} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-overlay"
          onClose={() => {
            setDeleteOrgPrompt(null);
            setDeleteConfirmText('');
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-12px transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    Are you sure?
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete this organization? This
                      action cannot be undone. Please type &apos;permanently
                      delete&apos; to confirm.
                    </p>
                    <TextInput
                      id="deleteConfirmText"
                      type="text"
                      name="deleteConfirmText"
                      data-qa="deleteConfirmText"
                      aria-label="deleteConfirmText"
                      onChange={(e) =>
                        setDeleteConfirmText(e.currentTarget.value)
                      }
                      autoFocus
                      value={deleteConfirmText}
                      required
                      autoComplete="off"
                    />
                  </div>

                  <div className="mt-4">
                    <LegacyButton
                      variant={LegacyButtonVariant.PRIMARY_LIGHT}
                      onClick={() => deleteOrganization()}
                      disabled={
                        isDeletingOrg ||
                        deleteConfirmText !== 'permanently delete'
                      }
                      className="mt-4"
                    >
                      Delete
                    </LegacyButton>

                    <LegacyButton
                      variant={LegacyButtonVariant.PRIMARY_LIGHT}
                      onClick={() => {
                        setDeleteOrgPrompt(null);
                        setDeleteConfirmText('');
                      }}
                      disabled={isDeletingOrg}
                      className="mt-4 ml-4"
                    >
                      Cancel
                    </LegacyButton>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Error message modal */}
      <Transition appear show={!!errorMessage} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-overlay"
          onClose={() => setErrorMessage(null)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-12px transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    Error
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">{errorMessage}</p>
                  </div>

                  <div className="mt-4">
                    <LegacyButton
                      variant={LegacyButtonVariant.PRIMARY_LIGHT}
                      onClick={() => setErrorMessage(null)}
                      className="mt-4"
                    >
                      Close
                    </LegacyButton>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </AdminLayout>
  );
};
